import { CalculationTableHeaderCellSize, IconName } from 'src/components/atoms'
import { Cell as Column } from 'src/components/molecules/CalculationTableHeaderRow'
import { OptionGroup } from 'src/components/molecules/SelectWithGroupOption'

import { parseCalculationDSLsToTokens } from 'src/slices/calculation/parser'
import Token from 'src/slices/calculation/token'
import TokenType from 'src/slices/calculation/token_type'
import { EditOCRFormat } from 'src/slices/editCalculations/editCalculationsSlice'
import { CalculationDSL, CalculationItem } from 'src/slices/services/api'
import { groupBy, injectIndex } from 'src/utils/array'
import { Cell as SelectCell } from '../components/EditCalculationDSLBodyRow'
import { BodyRow } from '../components/EditCalculationDSLTable'

// NOTE: 一度に追加させる cell のセットが [開きカッコ, 名前, 閉じカッコ, 四則演算]の4つなので
export const CELL_SET_COUNT = 4

export type RowFieldValue = {
  [key: string]: string | undefined
}

export type FieldValues = {
  rows: RowFieldValue[][]
}

type FieldName = 'open' | 'value' | 'close' | 'symbol'

export const FieldNameList: FieldName[] = ['open', 'value', 'close', 'symbol']

type FormNameWithValue = {
  name: string
  value?: string | number
}

type TableData = {
  rows: BodyRow[]
  maxCellSetCount: number
}

interface EditCalculationDSLFunctionsIf {
  columns(addCellSetCount?: number): Column[]
  tableData(
    calculationItems: CalculationItem[],
    editOCRFormats: EditOCRFormat[],
    restoredEditCalculationDSLs?: FormNameWithValue[][]
  ): TableData
  convertFormValuesToDSLs(values: FieldValues): string[]
  convertEditDataToFormValues(
    dsls: CalculationDSL[],
    calculationItems: CalculationItem[],
    editOCRFormats: EditOCRFormat[]
  ): FormNameWithValue[][]
}

const emptyColumnSet = [
  {
    text: '',
    size: 'large' as CalculationTableHeaderCellSize,
  },
  {
    text: '',
    size: 'small' as CalculationTableHeaderCellSize,
  },
  {
    text: '',
    size: 'small' as CalculationTableHeaderCellSize,
  },
  {
    text: '',
    size: 'small' as CalculationTableHeaderCellSize,
  },
]

const baseColumns = (addCellSetCount = 0): Column[] => {
  const base = [
    {
      text: 'No.',
      size: 'medium' as CalculationTableHeaderCellSize,
    },
    {
      text: '算出項目',
      size: 'large' as CalculationTableHeaderCellSize,
    },
    {
      text: '',
      size: 'small' as CalculationTableHeaderCellSize,
    },
    {
      text: '読取項目',
      size: 'large' as CalculationTableHeaderCellSize,
      colSpan: 2,
    },
    {
      text: '',
      size: 'small' as CalculationTableHeaderCellSize,
    },
    {
      text: '',
      size: 'small' as CalculationTableHeaderCellSize,
    },
    {
      text: '',
      size: 'small' as CalculationTableHeaderCellSize,
    },
  ]

  const emptyColumns = Array(addCellSetCount).fill(emptyColumnSet).flat()

  return injectIndex([...base, ...emptyColumns])
}

const openBracket: OptionGroup[] = [
  {
    options: [
      { title: '', value: '' },
      { title: '(', value: '((' },
    ],
  },
]

const closeBracket: OptionGroup[] = [
  {
    options: [
      { title: '', value: '' },
      { title: ')', value: '))' },
    ],
  },
]

const operatorSymbols: OptionGroup[] = [
  {
    options: [
      { title: '', value: '' },
      { title: '+', value: '+' },
      { title: '-', value: '-' },
      { title: '×', value: '*' },
      { title: '÷', value: '/' },
    ],
  },
]

const generateOptionGroup = (
  calculationItems: CalculationItem[],
  editOCRFormats: EditOCRFormat[],
  currentCalculationItemId: string,
  referencedCalculationItemIds: string[]
): OptionGroup[] => {
  const readItemOptions = editOCRFormats
    .filter((item) => item.calculationId === currentCalculationItemId)
    .map((item) => {
      return {
        // TODO: state の型修正後に修正
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        title: item.readItem!,
        value: `{{${item.id}}}`,
      }
    })
  const readItemOptionGroup = {
    title: '読取項目',
    isIndent: true,
    options: readItemOptions,
  }
  const calculationOptionGroup = {
    title: '算出項目',
    options: calculationItems
      .filter(
        (item) =>
          item.id !== currentCalculationItemId &&
          !referencedCalculationItemIds.includes(item.id)
      )
      .map((item) => {
        return {
          title: item.name,
          value: `<<${item.id}>>`,
        }
      }),
  }
  const deselect = {
    options: [
      {
        title: '',
        value: '',
      },
    ],
  }
  const calculator = {
    options: [
      {
        title: '数値を入力する',
        value: 'inputNumber',
        icon: 'calculator' as IconName,
      },
    ],
  }

  if (readItemOptions.length > 0) {
    return [deselect, readItemOptionGroup, calculationOptionGroup, calculator]
  }

  return [deselect, calculationOptionGroup, calculator]
}

const generateSelectCell = (
  name: string,
  options: OptionGroup[],
  size: 'medium' | 'large',
  hasFixedDSL: boolean,
  defaultValues: FormNameWithValue[] = []
): SelectCell => {
  const defaultValue = defaultValues.find((value) => value.name === name)
  return {
    name,
    options,
    size,
    defaultValue: hasFixedDSL ? undefined : defaultValue?.value,
  }
}

const convertTokenToValue = (
  token: Token,
  editOCRFormatId?: string
): string => {
  return editOCRFormatId ? `{{${editOCRFormatId}}}` : token.raw
}

const isSimpleFormula = (tokens: Token[]): boolean => {
  const allowedSymbols = ['+', '-']
  const operationTokens = tokens.filter(
    (token) => token.tokenType === TokenType.Operation
  )

  return operationTokens.every((token) =>
    allowedSymbols.includes(token.toDisplayString())
  )
}

const convertEditOCRFormatToFormValue = (
  editOCRFormat: EditOCRFormat,
  rowIndex: number,
  cellSetIndex: number
): FormNameWithValue[] => {
  const valueName = `rows.${rowIndex}.${cellSetIndex}.value`
  // TODO: 括弧をつける処理は convertItemIdToDSLId 的な関数に閉じる
  const valueEntry = {
    value: `{{${editOCRFormat.id}}}`,
    name: valueName,
  }

  // NOTE: cellSetIndex = 0 の場合は、それよりも左に式がないため、四則演算子が不要
  if (cellSetIndex > 0) {
    // TODO: getFormName 的な関数に閉じる
    const symbolName = `rows.${rowIndex}.${cellSetIndex - 1}.symbol`
    return [{ value: '+', name: symbolName }, valueEntry]
  }

  return [valueEntry]
}

export const generateCellSet = (
  rowIndex: number,
  cellIndex: number,
  calculationItems: CalculationItem[],
  editOCRFormats: EditOCRFormat[],
  calculationItemId: string,
  defaultValues?: FormNameWithValue[]
): SelectCell[] => {
  const calculationItemsWithFixedDSLs = calculationItems.filter(
    (calculationItem) => calculationItem.fixedDsl
  )

  const referencedCalculationItemIds = calculationItemsWithFixedDSLs
    .filter((calculationItemsWithFixedDSL) =>
      calculationItemsWithFixedDSL.fixedDsl?.includes(calculationItemId)
    )
    .map((calculationItemsWithFixedDSL) => calculationItemsWithFixedDSL.id)

  const name = (prefix: string) => {
    return `rows.${rowIndex}.${cellIndex / CELL_SET_COUNT}.${prefix}`
  }
  const cellFixedDsl = calculationItems.find(
    (calculationItem) => calculationItem.id === calculationItemId
  )?.fixedDsl
  const hasFixedDSL = Boolean(cellFixedDsl)

  return [
    generateSelectCell(
      name('open'),
      openBracket,
      'medium',
      hasFixedDSL,
      defaultValues
    ),
    generateSelectCell(
      name('value'),
      generateOptionGroup(
        calculationItems,
        editOCRFormats,
        calculationItemId,
        referencedCalculationItemIds
      ),
      'large',
      hasFixedDSL,
      defaultValues
    ),
    generateSelectCell(
      name('close'),
      closeBracket,
      'medium',
      hasFixedDSL,
      defaultValues
    ),
    generateSelectCell(
      name('symbol'),
      operatorSymbols,
      'medium',
      hasFixedDSL,
      defaultValues
    ),
  ]
}

const generatedDefaultCalculationDSLCells = (
  rowIndex: number,
  calculationItems: CalculationItem[],
  editOCRFormats: EditOCRFormat[],
  calculationItemId: string,
  defaultValues: FormNameWithValue[],
  cellIndex = 0
): SelectCell[] => {
  return generateCellSet(
    rowIndex,
    cellIndex,
    calculationItems,
    editOCRFormats,
    calculationItemId,
    defaultValues
  )
}

const generateCalculationDSLRow = (
  rowIndex: number,
  calculationItems: CalculationItem[],
  editOCRFormats: EditOCRFormat[],
  calculationItemId: string,
  defaultValues: FormNameWithValue[] = []
): BodyRow => {
  const fixedDsl = calculationItems.find(
    (item) => item.id === calculationItemId
  )?.fixedDsl
  const hasFixedDSL = Boolean(fixedDsl)
  const disabledRow = hasFixedDSL

  const calculationNameList = calculationItems.map(
    (calculationItem) => calculationItem.name
  )

  const cellSetCount = defaultValues.filter((fieldValues) => {
    return fieldValues.name.match(/value/)
  }).length

  const cellLength = hasFixedDSL ? 1 : cellSetCount || 1

  const cells = new Array(cellLength).fill(null).flatMap((_, i) => {
    return generatedDefaultCalculationDSLCells(
      rowIndex,
      calculationItems,
      editOCRFormats,
      calculationItemId,
      hasFixedDSL ? [] : defaultValues,
      i * CELL_SET_COUNT
    )
  })

  return {
    cells,
    title: calculationNameList[rowIndex],
    number: `${rowIndex + 1}`,
    disabledRow,
    // NOTE: 画面を開いた時には、四則演算子が埋まっていないため、プラスボタンは非活性
    disabledAddButton: true,
  }
}

const tokenTypeToFieldName = (type: TokenType): FieldName => {
  switch (type) {
    case TokenType.Operation:
      return 'symbol'
    case TokenType.LeftParenthesis:
      return 'open'
    case TokenType.RightParenthesis:
      return 'close'
    case TokenType.ReadItem:
    case TokenType.CalculationItem:
    case TokenType.Number:
    default:
      return 'value'
  }
}

const convertFromOnlyEditData = (
  calculationItems: CalculationItem[],
  groupedEditOCRFormats: Map<string, EditOCRFormat[]>
): FormNameWithValue[][] => {
  return calculationItems.map((calculationItem, rowIndex) => {
    const calculationId = calculationItem.id
    const editOCRFormatsInRow = groupedEditOCRFormats.get(calculationId)

    if (!editOCRFormatsInRow) return []

    return editOCRFormatsInRow.flatMap((editOCRFormat, cellIndex) => {
      return convertEditOCRFormatToFormValue(editOCRFormat, rowIndex, cellIndex)
    })
  })
}

// FIXME: リファクタリングが必要
const convertFromReferenceRestoreDSL = (
  dsls: CalculationDSL[],
  groupedEditOCRFormats: Map<string, EditOCRFormat[]>
): FormNameWithValue[][] => {
  const dslsToParse = dsls.map((dsl) => {
    return {
      ...dsl,
      readItems: (dsl.ocrFormats ?? []).map((format) => {
        return {
          id: format.id,
          name: format.readItem,
        }
      }),
    }
  })

  const parseDSLs = parseCalculationDSLsToTokens(dslsToParse)

  const dslValues = parseDSLs
    .map((tokens, rowIndex) => {
      const calculationId = dsls[rowIndex].calculationItem.id

      type ReduceState = {
        formValues: FormNameWithValue[]
        cellSetIndex: number
        skip: boolean
        remainingFormats: EditOCRFormat[]
      }

      const initialState: ReduceState = {
        formValues: [],
        cellSetIndex: 0,
        skip: false,
        remainingFormats: [...(groupedEditOCRFormats.get(calculationId) ?? [])],
      }

      const {
        formValues: resultFormValues,
        remainingFormats: editOCRFormatsInRow,
      } = tokens.reduce<ReduceState>((acc, token) => {
        // NOTE: editOCRFormatId の場合、 DSL で使われている readItemId と違うため、
        // raw データを使わず、 editOCRFormats から display string で find する
        const editOCRFormatId = acc.remainingFormats.find(
          (format) => format.readItem === token.toDisplayString()
        )?.id

        const fieldName = tokenTypeToFieldName(token.tokenType)

        // NOTE: cellSet は [開き括弧, value, 閉じ括弧, 四則演算子] までが一つのセットなので
        // 一度 value で return をした場合は ( = step1 で入力項目を削除 or 名前を変更した時)
        // 次の四則演算子までスキップしたい、
        // そのため、 'open' or 'value' がきたらスキップフラグを落とすようにしている
        let { skip } = acc
        if (skip && (fieldName === 'open' || fieldName === 'value')) {
          skip = false
        }

        // NOTE: ReadItem でかつ editOCRFormatId が見つからない時は、
        // DSL にある読取項目名の名前を変更した時 or DSL にある読取項目名を削除した時、
        // なので DB に登録されている DSL と関係ないため、 return する
        // また、 DSL がない場合もスキップする
        if (
          (token.tokenType === TokenType.ReadItem && !editOCRFormatId) ||
          token.raw === '0' ||
          skip
        ) {
          return { ...acc, skip: true }
        }

        // NOTE:
        // DSL がない場合 DB には "0" で登録される。
        // しかし画面に 0 と表示すると、数字の 0 なのか、何もないという意味の 0 なのかがわからないため、
        // 0 の場合は、画面に何かが表示されないようにするため、undefined とする
        const value =
          token.raw === '0'
            ? undefined
            : convertTokenToValue(token, editOCRFormatId)
        const name = `rows.${rowIndex}.${acc.cellSetIndex}.${fieldName}`

        // NOTE:
        // DSL の読取項目名と step1 の読取項目名が一致している場合、
        // 重複してしまうため、 remainingFormats から取り除く
        const remainingFormats = acc.remainingFormats.filter(
          (format) => `{{${format.id}}}` !== value
        )

        const cellSetIndex =
          token.tokenType === TokenType.Operation
            ? acc.cellSetIndex + 1
            : acc.cellSetIndex

        return {
          formValues: [...acc.formValues, { value, name }],
          cellSetIndex,
          skip: false,
          remainingFormats,
        }
      }, initialState)

      if (isSimpleFormula(tokens) && editOCRFormatsInRow.length > 0) {
        const originalCellSetCount = resultFormValues.filter((value) =>
          value.name.endsWith('value')
        ).length
        const addedFormValues = editOCRFormatsInRow.flatMap(
          (editOCRFormat, index) => {
            const cellIndex = originalCellSetCount + index
            return convertEditOCRFormatToFormValue(
              editOCRFormat,
              rowIndex,
              cellIndex
            )
          }
        )

        return resultFormValues.concat(addedFormValues)
      }

      return resultFormValues
    })
    .map((formValues) => {
      // NOTE: 最後の formValue が四則演算子だった場合
      // 最後 formValue を削除する
      if (formValues[formValues.length - 1]?.name.endsWith('symbol')) {
        return formValues.slice(0, -1)
      }

      return formValues
    })

  return dslValues
}

const EditCalculationDSLFunctions: EditCalculationDSLFunctionsIf = {
  columns(addCellSetCount?: number): Column[] {
    return baseColumns(addCellSetCount)
  },
  tableData(
    calculationItems,
    editOCRFormats,
    restoredEditCalculationDSLs = []
  ) {
    const rows = new Array(calculationItems.length)
      .fill(null)
      .map((_, i) =>
        generateCalculationDSLRow(
          i,
          calculationItems,
          editOCRFormats,
          calculationItems[i].id,
          restoredEditCalculationDSLs[i]
        )
      )
    const maxCellSetCount =
      rows.length > 0
        ? Math.max(...rows.map((row) => row.cells.length)) / CELL_SET_COUNT
        : 1
    return {
      rows,
      maxCellSetCount,
    }
  },
  convertFormValuesToDSLs(values) {
    return values.rows.map((row) => {
      return row
        .flatMap((formValue) => {
          return Object.values(formValue)
        })
        .join('')
    })
  },
  convertEditDataToFormValues(dsls, calculationItems, editOCRFormats) {
    const groupedEditOCRFormats = groupBy<EditOCRFormat>(
      editOCRFormats,
      (editOCRFormat: EditOCRFormat) => editOCRFormat.calculationId
    )

    // DSL がない場合
    if (dsls.length === 0) {
      return convertFromOnlyEditData(calculationItems, groupedEditOCRFormats)
    }

    // DSL がある場合
    return convertFromReferenceRestoreDSL(dsls, groupedEditOCRFormats)
  },
}

export default EditCalculationDSLFunctions
